import { useCallback, useEffect, useMemo } from 'react'
import { useQuery } from '@apollo/client'

import type { GetPostsByTerritoryQuery } from 'generated/graphql'
import { datadog } from 'utils'

import { DEFAULT_OPTIONS, GET_POSTS_BY_TERRITORY_LOG } from './consts'
import { GET_POSTS_BY_TERRITORY } from './operations'
import type { GetPostsByTerritoryVariables, UsePostsByTerritoryOptions } from './types'

export function usePostsByTerritory(
  territoryId: string,
  { timeRange, limit, publishedAt, providers }: UsePostsByTerritoryOptions = DEFAULT_OPTIONS
) {
  const queryVariables = useMemo<GetPostsByTerritoryVariables>(() => {
    const hasFilters = timeRange || publishedAt || providers

    return {
      territoryId,
      limit,
      ...(hasFilters && { filters: { timeRange, publishedAt, providers } }),
    }
  }, [territoryId, timeRange, limit, publishedAt, providers])

  const {
    data,
    loading,
    error,
    called,
    variables: apolloVariables,
  } = useQuery<GetPostsByTerritoryQuery>(GET_POSTS_BY_TERRITORY, {
    fetchPolicy: 'cache-first',
    variables: queryVariables,
  })

  const handleErrors = useCallback(() => {
    if (!called || loading || !error) return

    datadog.dispatchRequestErrorLog({
      log: GET_POSTS_BY_TERRITORY_LOG,
      query: { name: 'getPostsByTerritory', variables: apolloVariables },
      error,
    })
  }, [apolloVariables, called, error, loading])

  useEffect(handleErrors, [handleErrors])

  const posts = useMemo(() => {
    return data?.getTerritory?.territoryMostEngaged?.posts?.map(item => {
      return {
        ...item,
        alt: item?.title,
      }
    })
  }, [data?.getTerritory?.territoryMostEngaged?.posts])
  const isLoading = loading

  const memoizedValue = useMemo(() => ({ posts, isLoading, error }), [posts, isLoading, error])

  return memoizedValue
}
